import { useState, type FormEvent } from 'react';
import type {
  ExpenseCategory,
  ExpenseSource,
  SolTemporalKpiSnapshot,
} from '@economi/domain-contracts';
import {
  centsToSoles,
  classifyExpenseHorizonForCardInCycle,
  formatSolesFromCents,
  resolveCardBillingCloseDateForCycle,
  resolveExpenseReceiptImageUrl,
  simulateExpenseImpact,
  type BillingCycleHorizon,
  type ExpenseLedgerItem,
  type FinancialCycleState,
  type PaymentMethodType,
} from '../../services/solImpactEngine';
import { InfoPopover } from '../InfoPopover/InfoPopover';
import styles from './ExpenseQuickLedger.module.css';

interface ExpenseQuickLedgerProps {
  readonly snapshot: SolTemporalKpiSnapshot;
  readonly expenses: ReadonlyArray<ExpenseLedgerItem>;
  readonly cycleState?: FinancialCycleState;
  readonly onAddExpense: (params: {
    amountSoles: number;
    merchantName: string;
    category: ExpenseCategory;
    description: string;
    source: ExpenseSource;
    occurredAtIso?: string;
    paymentMethod?: PaymentMethodType;
    creditCardId?: string | null;
    cardLastFourDigits?: string | null;
    billingCycleHorizon?: BillingCycleHorizon;
  }) => void;
  readonly onUpdateExpense?: (
    expenseId: string,
    updates: {
      merchantName?: string;
      issuerRuc?: string | null;
      receiptNumber?: string | null;
      occurredAtIso?: string;
      category?: ExpenseCategory;
      description?: string;
      amountSoles?: number;
      isInevitable?: boolean;
      creditCardId?: string | null;
      cardLastFourDigits?: string | null;
    }
  ) => void;
  readonly onDeleteExpense?: (expenseId: string) => void;
}

const CATEGORY_LABELS: Record<ExpenseCategory, string> = {
  FOOD_DINING: 'Alimentación / Restaurantes',
  GROCERIES: 'Supermercado / Bodega',
  TRANSPORT: 'Transporte / Combustible',
  UTILITIES: 'Servicios (Luz, Agua, Fibra)',
  HOUSING: 'Vivienda / Alquiler',
  HEALTH: 'Salud / Farmacia',
  ENTERTAINMENT: 'Entretenimiento / Ocio',
  SHOPPING: 'Compras / Retail',
  EDUCATION: 'Educación / Libros',
  FINANCIAL: 'Comisiones / Seguros',
  OTHER: 'Otros Gastos',
};

function getSourceBadge(source: ExpenseSource): { text: string; className: string } {
  switch (source) {
    case 'RECEIPT_OCR':
      return { text: 'VISION AI · R2', className: styles.sourceOcr ?? '' };
    case 'PUBLIC_API':
      return { text: 'API REST v1', className: styles.sourceApi ?? '' };
    case 'RECURRING_RULE':
      return { text: 'GASTO RECURRENTE', className: styles.sourceApi ?? '' };
    case 'WEB_MANUAL':
      return { text: 'MANUAL WEB', className: styles.sourceManual ?? '' };
  }
}

export function ExpenseQuickLedger({
  snapshot,
  expenses,
  cycleState,
  onAddExpense,
  onUpdateExpense,
  onDeleteExpense,
}: ExpenseQuickLedgerProps) {
  const [merchantName, setMerchantName] = useState<string>('');
  const [amountSoles, setAmountSoles] = useState<string>('');
  const [occurredAtIso, setOccurredAtIso] = useState<string>(() =>
    new Date().toISOString().slice(0, 10)
  );
  const [category, setCategory] = useState<ExpenseCategory>('FOOD_DINING');
  const [selectedCardId, setSelectedCardId] = useState<string>('');
  const [description, setDescription] = useState<string>('');
  const [previewExpenseId, setPreviewExpenseId] = useState<string | null>(null);
  const [failedImageIds, setFailedImageIds] = useState<Record<string, boolean>>(
    {}
  );
  const [editingRowId, setEditingRowId] = useState<string | null>(null);
  const [editRowMerchant, setEditRowMerchant] = useState<string>('');
  const [editRowAmountSoles, setEditRowAmountSoles] = useState<string>('');
  const [editRowDateIso, setEditRowDateIso] = useState<string>('');
  const [editRowCardId, setEditRowCardId] = useState<string>('');

  const startEditingRow = (item: ExpenseLedgerItem) => {
    setEditingRowId(item.id);
    setEditRowMerchant(item.merchantName);
    setEditRowAmountSoles(String(centsToSoles(item.amountCents)));
    setEditRowDateIso(item.occurredAtIso.slice(0, 10));
    setEditRowCardId(item.creditCardId ?? '');
  };

  const handleSaveRowEdit = (item: ExpenseLedgerItem) => {
    if (!onUpdateExpense) return;
    const cleanedAmount = String(editRowAmountSoles ?? '')
      .trim()
      .replace(/[^0-9.,]/g, '')
      .replace(',', '.');
    const parsedSoles = Number(cleanedAmount);
    const nextAmountSoles =
      Number.isFinite(parsedSoles) && parsedSoles > 0
        ? Math.max(0.01, parsedSoles)
        : centsToSoles(item.amountCents);
    const nextCardId = editRowCardId.trim() || null;
    const pickedCard = nextCardId
      ? configuredCards.find((c) => c.id === nextCardId) ?? null
      : null;

    onUpdateExpense(item.id, {
      merchantName: editRowMerchant.trim() || item.merchantName,
      amountSoles: nextAmountSoles,
      occurredAtIso:
        editRowDateIso.trim().slice(0, 10) || item.occurredAtIso.slice(0, 10),
      creditCardId: nextCardId,
      cardLastFourDigits:
        pickedCard?.lastFourDigits || item.cardLastFourDigits || null,
    });
    setEditingRowId(null);
  };

  const configuredCards = cycleState?.creditCards ?? [];
  const selectedCard = selectedCardId
    ? configuredCards.find((c) => c.id === selectedCardId) ?? null
    : null;

  const selectedHorizon: BillingCycleHorizon =
    cycleState && selectedCard
      ? classifyExpenseHorizonForCardInCycle(
          cycleState,
          occurredAtIso,
          selectedCard.id
        )
      : 'CURRENT_CYCLE';

  const selectedCardCloseDateIso =
    cycleState && selectedCard
      ? resolveCardBillingCloseDateForCycle(
          selectedCard.billingCloseDayOfMonth,
          cycleState.startDateIso,
          cycleState.renewalDateIso
        )
      : null;

  const previewExpense =
    expenses.find((exp) => exp.id === previewExpenseId) ?? null;

  const numericSoles = Math.max(0, Number(amountSoles) || 0);
  const impact = simulateExpenseImpact(snapshot, numericSoles);

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    if (numericSoles <= 0) return;

    onAddExpense({
      amountSoles: numericSoles,
      merchantName: merchantName.trim() || 'Comercio Registrado en Día',
      category,
      description:
        description.trim() || `Registro directo (${CATEGORY_LABELS[category]})`,
      source: 'WEB_MANUAL',
      occurredAtIso: occurredAtIso.slice(0, 10),
      paymentMethod: 'CREDIT_CARD',
      creditCardId: selectedCard?.id ?? null,
      cardLastFourDigits: selectedCard?.lastFourDigits || null,
      billingCycleHorizon: selectedHorizon,
    });

    setMerchantName('');
    setAmountSoles('');
    setDescription('');
  };

  return (
    <div className={styles.layout}>
      <div className={styles.card}>
        <div>
          <h3 className={styles.cardTitle}>
            Registro Rápido de Gasto{' '}
            <InfoPopover title="Registro Rápido sin Imagen">
              Se agrupa según su fecha de emisión y el cierre de facturación de la tarjeta usada.
            </InfoPopover>
          </h3>
        </div>

        <form className={styles.form} onSubmit={handleSubmit}>
          <div className={styles.fieldGroup}>
            <label htmlFor="exp-merchant" className={styles.label}>
              Comercio o Establecimiento
            </label>
            <input
              id="exp-merchant"
              type="text"
              placeholder="Ej. Panadería San Antonio, Tambo+, Uber"
              value={merchantName}
              onChange={(e) => setMerchantName(e.target.value)}
              className={styles.input}
              required
            />
          </div>

          <div className={styles.fieldGroup}>
            <label htmlFor="exp-amount" className={styles.label}>
              Monto en Soles (PEN)
            </label>
            <input
              id="exp-amount"
              type="number"
              min="0.01"
              step="0.01"
              placeholder="Ej. 25.50"
              value={amountSoles}
              onChange={(e) => setAmountSoles(e.target.value)}
              className={styles.input}
              required
            />
          </div>

          <div className={styles.fieldGroup}>
            <label htmlFor="exp-payment-method" className={styles.label}>
              💳 Tarjeta de Crédito Utilizada (incluye Yape / Plin con cargo a tarjeta)
            </label>
            <select
              id="exp-payment-method"
              value={selectedCardId}
              onChange={(e) => setSelectedCardId(e.target.value)}
              className={styles.select}
            >
              <option value="">
                — Selecciona la tarjeta de crédito usada —
              </option>
              {configuredCards.map((card) => (
                <option key={card.id} value={card.id}>
                  💳 {card.alias} ({card.issuerBank}
                  {card.lastFourDigits ? ` · *${card.lastFourDigits}` : ''}) · Factura día{' '}
                  {card.billingCloseDayOfMonth}
                </option>
              ))}
            </select>
          </div>

          <div className={styles.fieldGroup}>
            <label htmlFor="exp-issued-date" className={styles.label}>
              Fecha de Emisión / Gasto
            </label>
            <input
              id="exp-issued-date"
              type="date"
              value={occurredAtIso}
              onChange={(e) => setOccurredAtIso(e.target.value)}
              className={styles.input}
              required
            />
          </div>

          <div className={styles.fieldGroup}>
            <label htmlFor="exp-category" className={styles.label}>
              Categoría Presupuestaria
            </label>
            <select
              id="exp-category"
              value={category}
              onChange={(e) => setCategory(e.target.value as ExpenseCategory)}
              className={styles.select}
            >
              {(Object.keys(CATEGORY_LABELS) as ExpenseCategory[]).map((catKey) => (
                <option key={catKey} value={catKey}>
                  {CATEGORY_LABELS[catKey]}
                </option>
              ))}
            </select>
          </div>

          <div className={styles.fieldGroup}>
            <label htmlFor="exp-desc" className={styles.label}>
              Nota / Concepto (Opcional)
            </label>
            <input
              id="exp-desc"
              type="text"
              placeholder="Ej. Café y sándwich antes de reunión"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className={styles.input}
            />
          </div>

          <div className={styles.previewPill}>
            {selectedHorizon === 'NEXT_CYCLE' ? (
              <span style={{ color: '#38bdf8' }}>
                🔵 <strong>Impacta en Próximo Ciclo:</strong> la facturación de{' '}
                <strong>{selectedCard?.alias}</strong> cerró el{' '}
                <strong>{selectedCardCloseDateIso}</strong> — no reduce tu cuota de este ciclo.
              </span>
            ) : (
              <>
                <span>
                  Impacto inmediato en Cuota Diaria:{' '}
                  <strong style={{ color: 'var(--status-warning)' }}>
                    -{formatSolesFromCents(impact.dailyAllowanceReductionCents)}/día
                  </strong>
                </span>
                <span>
                  Nueva cuota segura:{' '}
                  <strong style={{ color: 'var(--accent-primary)' }}>
                    {formatSolesFromCents(impact.newSafeDailyAllowanceCents)}/día
                  </strong>{' '}
                  ({impact.budgetTimeCostHours} hrs consumidas)
                </span>
              </>
            )}
          </div>

          <button type="submit" className={styles.submitBtn}>
            + Registrar Gasto ({formatSolesFromCents(impact.proposedExpenseCents)})
          </button>
        </form>
      </div>

      <div className={styles.card}>
        <div>
          <h3 className={styles.cardTitle}>
            Bitácora de Gastos ({expenses.length}){' '}
            <InfoPopover title="Bitácora de Gastos del Ciclo">
              Cada asiento muestra su fecha de emisión real, tarjeta usada, ciclo de facturación y compresión diaria.
            </InfoPopover>
          </h3>
        </div>

        <div className={styles.ledgerList}>
          {expenses.length === 0 ? (
            <div className={styles.previewPill} style={{ textAlign: 'center', padding: '1.1rem' }}>
              Aún no hay gastos registrados en este ciclo.
            </div>
          ) : (
            expenses.map((item) => {
              const badge = getSourceBadge(item.source);
              const rawReceiptUrl = resolveExpenseReceiptImageUrl(item);
              const receiptUrl =
                rawReceiptUrl && !failedImageIds[item.id]
                  ? rawReceiptUrl
                  : null;
              const itemCard = item.creditCardId
                ? configuredCards.find((c) => c.id === item.creditCardId) ?? null
                : null;
              const itemHorizon = item.billingCycleHorizon ?? 'CURRENT_CYCLE';
              return (
                <div key={item.id} className={styles.ledgerItem}>
                  <div className={styles.ledgerLeftWithThumb}>
                    {receiptUrl && (
                      <button
                        type="button"
                        className={styles.quickReceiptThumbBtn}
                        onClick={() => setPreviewExpenseId(item.id)}
                        title="Ver imagen de boleta / recibo escaneado"
                      >
                        <img
                          src={receiptUrl}
                          alt={`Boleta de ${item.merchantName}`}
                          className={styles.quickReceiptThumbImg}
                          loading="lazy"
                          onError={() =>
                            setFailedImageIds((prev) => ({
                              ...prev,
                              [item.id]: true,
                            }))
                          }
                        />
                      </button>
                    )}

                    <div className={styles.ledgerLeft}>
                      <div className={styles.merchantRow}>
                        <span className={styles.merchantName}>
                          {item.merchantName}
                        </span>
                        {item.issuerRuc && (
                          <span className={styles.sourceBadge}>
                            RUC {item.issuerRuc}
                          </span>
                        )}
                        {item.receiptNumber && (
                          <span className={styles.sourceBadge}>
                            🧾 {item.receiptNumber}
                          </span>
                        )}
                        <span className={`${styles.sourceBadge} ${badge.className}`}>
                          {badge.text}
                        </span>
                        <span className={`${styles.sourceBadge} ${styles.sourceManual ?? ''}`}>
                          {itemCard
                            ? `💳 ${itemCard.alias}${
                                itemCard.lastFourDigits
                                  ? ` (*${itemCard.lastFourDigits})`
                                  : ''
                              }`
                            : '⚠️ Sin tarjeta asignada'}
                        </span>
                        {!itemCard && onUpdateExpense && configuredCards.length > 0 && (
                          <select
                            value={item.creditCardId ?? ''}
                            onChange={(e) => {
                              const nextCardId = e.target.value || null;
                              const picked = configuredCards.find(
                                (c) => c.id === nextCardId
                              );
                              onUpdateExpense(item.id, {
                                creditCardId: nextCardId,
                                cardLastFourDigits:
                                  picked?.lastFourDigits || null,
                              });
                            }}
                            className={styles.select}
                            style={{ padding: '0.18rem 0.45rem', fontSize: '0.7rem' }}
                            aria-label="Asignar tarjeta de crédito al gasto"
                          >
                            <option value="">✏️ Asignar tarjeta...</option>
                            {configuredCards.map((c) => (
                              <option key={c.id} value={c.id}>
                                💳 {c.alias}
                                {c.lastFourDigits ? ` (*${c.lastFourDigits})` : ''}
                              </option>
                            ))}
                          </select>
                        )}
                        {itemHorizon === 'NEXT_CYCLE' && (
                          <span className={`${styles.sourceBadge} ${styles.sourceApi ?? ''}`}>
                            🔵 PRÓXIMO CICLO
                          </span>
                        )}
                      </div>
                      <span className={styles.ledgerMeta}>
                        📅 Emisión: {item.occurredAtIso.slice(0, 10)} ·{' '}
                        {CATEGORY_LABELS[item.category]} · {item.description}
                      </span>

                      {editingRowId === item.id && onUpdateExpense && (
                        <div
                          style={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            alignItems: 'center',
                            gap: '0.45rem',
                            marginTop: '0.45rem',
                            padding: '0.5rem',
                            borderRadius: '8px',
                            background: '#eef4ff',
                            border: '1px solid #cbd5e1',
                          }}
                        >
                          <input
                            type="text"
                            value={editRowMerchant}
                            onChange={(e) => setEditRowMerchant(e.target.value)}
                            placeholder="Comercio"
                            className={styles.input}
                            style={{
                              width: '10.5rem',
                              padding: '0.28rem 0.45rem',
                              fontSize: '0.78rem',
                            }}
                            aria-label="Editar comercio"
                          />
                          <input
                            type="number"
                            step="0.01"
                            min="0.01"
                            value={editRowAmountSoles}
                            onChange={(e) =>
                              setEditRowAmountSoles(e.target.value)
                            }
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                handleSaveRowEdit(item);
                              }
                            }}
                            placeholder="Monto S/"
                            className={styles.input}
                            style={{
                              width: '6.5rem',
                              padding: '0.28rem 0.45rem',
                              fontSize: '0.78rem',
                              fontWeight: 700,
                            }}
                            aria-label="Editar monto en Soles"
                          />
                          <input
                            type="date"
                            value={editRowDateIso}
                            onChange={(e) => setEditRowDateIso(e.target.value)}
                            className={styles.input}
                            style={{
                              width: '8.4rem',
                              padding: '0.28rem 0.45rem',
                              fontSize: '0.78rem',
                            }}
                            aria-label="Editar fecha de emisión"
                          />
                          {configuredCards.length > 0 && (
                            <select
                              value={editRowCardId}
                              onChange={(e) => setEditRowCardId(e.target.value)}
                              className={styles.select}
                              style={{
                                padding: '0.28rem 0.45rem',
                                fontSize: '0.76rem',
                              }}
                              aria-label="Editar tarjeta de crédito"
                            >
                              <option value="">⚠️ Sin tarjeta</option>
                              {configuredCards.map((c) => (
                                <option key={c.id} value={c.id}>
                                  💳 {c.alias}
                                  {c.lastFourDigits
                                    ? ` (*${c.lastFourDigits})`
                                    : ''}
                                </option>
                              ))}
                            </select>
                          )}
                          <button
                            type="button"
                            className={styles.viewQuickReceiptBtn}
                            onClick={() => handleSaveRowEdit(item)}
                          >
                            💾 Guardar
                          </button>
                          <button
                            type="button"
                            className={styles.deleteExpenseBtn}
                            onClick={() => setEditingRowId(null)}
                          >
                            Cancelar
                          </button>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className={styles.ledgerRight}>
                    <span className={styles.expenseAmount}>
                      -{formatSolesFromCents(item.amountCents)}
                    </span>
                    <span className={styles.compressionTag}>
                      -{formatSolesFromCents(item.dailyCompressionCents)}/día ·{' '}
                      {item.budgetHoursConsumed}h
                    </span>
                    <div className={styles.quickActionsGroup}>
                      {receiptUrl && (
                        <button
                          type="button"
                          className={styles.viewQuickReceiptBtn}
                          onClick={() => setPreviewExpenseId(item.id)}
                        >
                          🖼️ Ver Boleta
                        </button>
                      )}
                      {onUpdateExpense && editingRowId !== item.id && (
                        <button
                          type="button"
                          className={styles.viewQuickReceiptBtn}
                          onClick={() => startEditingRow(item)}
                          title="Editar monto, comercio, fecha o tarjeta"
                        >
                          ✏️ Editar
                        </button>
                      )}
                      {onDeleteExpense && (
                        <button
                          type="button"
                          className={styles.deleteExpenseBtn}
                          onClick={() => onDeleteExpense(item.id)}
                          title="Eliminar gasto"
                        >
                          🗑️ Eliminar
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      {previewExpense &&
        (() => {
          const modalUrl = resolveExpenseReceiptImageUrl(previewExpense);
          return (
            <div
              className={styles.quickModalBackdrop}
              role="dialog"
              aria-modal="true"
              onClick={() => setPreviewExpenseId(null)}
            >
              <div
                className={styles.quickModalDialog}
                onClick={(e) => e.stopPropagation()}
              >
                <div className={styles.quickModalHeader}>
                  <div>
                    <strong className={styles.merchantName}>
                      {previewExpense.merchantName}
                    </strong>
                    <div className={styles.ledgerMeta}>
                      📅 Emisión: {previewExpense.occurredAtIso.slice(0, 10)} ·{' '}
                      {previewExpense.receiptNumber
                        ? `Comprobante ${previewExpense.receiptNumber} · `
                        : ''}
                      {previewExpense.issuerRuc
                        ? `RUC ${previewExpense.issuerRuc}`
                        : ''}
                    </div>
                  </div>
                  <button
                    type="button"
                    className={styles.deleteExpenseBtn}
                    onClick={() => setPreviewExpenseId(null)}
                  >
                    ✕ Cerrar
                  </button>
                </div>
                {modalUrl && (
                  <img
                    src={modalUrl}
                    alt={`Boleta de ${previewExpense.merchantName}`}
                    className={styles.quickModalImg}
                  />
                )}
              </div>
            </div>
          );
        })()}
    </div>
  );
}
